// FCM payload → in-app path resolver.
//
// Runs in two places:
//   1. The foreground onMessage handler (`use-fcm-foreground.ts`) — resolves
//      the path when the user clicks the in-app toast/notification so React
//      Router can navigate without reloading.
//   2. The service worker (`public/firebase-messaging-sw.js`) — a JS clone of
//      the same `type` → `path` table lives there too, because the SW cannot
//      import TypeScript. Keep the two in sync: any new type added here
//      MUST also be added to the SW switch, and vice versa.
//
// Keep this module pure: no React, no Firestore, no window. It has to be
// importable from anywhere (SW build step excluded).

export interface FcmDataPayload {
  type?: string
  bookingId?: string
  clubId?: string
  tab?: string
  [key: string]: string | undefined
}

/**
 * Resolve the in-app path for a given FCM data payload.
 *
 * Returns `null` when the payload carries no recognised `type` — callers
 * fall back to `/` (the current behaviour).
 *
 * Design: the path is URL-escaped and carries the resource id as a
 * `highlight` query param, so the landing screen can scroll-to + flash
 * that row. Pages opt in via `useHighlightFromQuery`.
 */
export function resolveFcmDeepLink(data: FcmDataPayload | undefined): string | null {
  if (!data || !data.type) return null
  switch (data.type) {
    case 'booking_created':
    case 'booking_updated':
    case 'booking_cancelled':
      if (data.bookingId) {
        return `/bookings?highlight=${encodeURIComponent(data.bookingId)}`
      }
      return '/bookings'
    case 'payment_collected':
      return '/ingresos'
    case 'feedback_received':
      return '/activity'
    case 'staff_invite_accepted':
      return '/staff'
    default:
      return null
  }
}
